import { useEffect, useMemo, useRef, useState } from "react";
import { ChatBubble } from "./flowChatBubbles";
import FlowChatwootChips from "./flowChatwootChips";
import { buildLiveBubbles } from "./flowTimelineView";
import { debounceEvidenceChips } from "./flowChatReplay";

const TICK_MS = 50;
const DEFAULT_SPEEDS = [1, 2, 4];

function rowKey(row) {
    return `${row.caseId}:${row.turnId}`;
}

export function timelineStartIso(timeline) {
    if (!Array.isArray(timeline)) return null;
    for (const event of timeline) {
        if (event?.at) return event.at;
    }
    return null;
}

export function virtualElapsedMs(t0Iso, nowMs, speed) {
    const t0 = Date.parse(t0Iso);
    if (!Number.isFinite(t0)) return 0;
    const spd = Number(speed);
    if (!Number.isFinite(spd) || spd <= 0) return 0;
    const delta = nowMs - t0;
    if (delta <= 0) return 0;
    return delta * spd;
}

export function visibleEvents(timeline, t0Iso, elapsedMs) {
    if (!Array.isArray(timeline) || !t0Iso) return [];
    const elapsed = Number(elapsedMs);
    if (!Number.isFinite(elapsed) || elapsed < 0) return [];
    const t0 = Date.parse(t0Iso);
    if (!Number.isFinite(t0)) return [];
    return timeline.filter((event) => {
        const at = Date.parse(event?.at);
        if (!Number.isFinite(at)) return false;
        return at - t0 <= elapsed;
    });
}

function timelineDurationMs(timeline, t0Iso) {
    const t0 = Date.parse(t0Iso);
    if (!Number.isFinite(t0) || !Array.isArray(timeline)) return 0;
    let max = 0;
    for (const event of timeline) {
        const at = Date.parse(event?.at);
        if (Number.isFinite(at)) max = Math.max(max, at - t0);
    }
    return max;
}

function eventOffsetMs(event, t0Iso) {
    const t0 = Date.parse(t0Iso);
    const at = Date.parse(event?.at);
    if (!Number.isFinite(t0) || !Number.isFinite(at)) return 0;
    return Math.max(0, at - t0);
}

/** Offset do último evento daquele turno (para scrub ao clicar no balão). */
export function turnScrubOffsetMs(timeline, t0Iso, turnId, caseId) {
    if (!t0Iso || turnId == null) return 0;
    let offset = 0;
    let found = false;
    for (const event of timeline || []) {
        if (String(event?.turnId) !== String(turnId)) continue;
        if (caseId != null && event.caseId != null && event.caseId !== caseId) continue;
        offset = eventOffsetMs(event, t0Iso);
        found = true;
    }
    return found ? offset : 0;
}

export default function FlowTimedReplay({
    timeline = [],
    results = [],
    cases = [],
    speeds = DEFAULT_SPEEDS,
    onClockChange,
}) {
    const safeTimeline = useMemo(
        () => (Array.isArray(timeline) ? timeline : []),
        [timeline]
    );
    const safeResults = useMemo(
        () => (Array.isArray(results) ? results : []),
        [results]
    );
    const speedOptions = Array.isArray(speeds) && speeds.length > 0 ? speeds : DEFAULT_SPEEDS;

    const t0Iso = useMemo(() => timelineStartIso(safeTimeline), [safeTimeline]);
    const totalMs = useMemo(
        () => (t0Iso ? timelineDurationMs(safeTimeline, t0Iso) : 0),
        [safeTimeline, t0Iso]
    );

    // null = ancorado no fim (estado final ao abrir / após concluir o play)
    const [elapsedMs, setElapsedMs] = useState(null);
    const [playing, setPlaying] = useState(false);
    const [speed, setSpeed] = useState(speedOptions[0]);
    const [selectedKey, setSelectedKey] = useState(null);

    const playAnchorMsRef = useRef(null);
    const elapsedAtPlayRef = useRef(0);

    const clockMs = elapsedMs == null ? totalMs : elapsedMs;

    const untilIso = useMemo(() => {
        if (!t0Iso) return null;
        const t0 = Date.parse(t0Iso);
        if (!Number.isFinite(t0)) return null;
        return new Date(t0 + Math.max(0, clockMs)).toISOString();
    }, [t0Iso, clockMs]);

    const visible = useMemo(
        () => (t0Iso ? visibleEvents(safeTimeline, t0Iso, clockMs) : []),
        [safeTimeline, t0Iso, clockMs]
    );

    const resultsForBubbles = useMemo(() => {
        const replyTurnIds = new Set(
            visible
                .filter((event) => event.kind === "bot_reply" && event.turnId != null)
                .map((event) => String(event.turnId))
        );
        return safeResults.filter((row) => replyTurnIds.has(String(row.turnId)));
    }, [visible, safeResults]);

    const bubbles = useMemo(
        () => buildLiveBubbles({ timeline: visible, results: resultsForBubbles }),
        [visible, resultsForBubbles]
    );

    const traceSteps = useMemo(
        () => visible.filter((e) => e.kind === "trace_step"),
        [visible]
    );
    const currentTrace = traceSteps.length > 0 ? traceSteps[traceSteps.length - 1] : null;

    const caseById = useMemo(() => {
        const map = new Map();
        for (const c of cases || []) {
            if (c?.id) map.set(c.id, c);
        }
        return map;
    }, [cases]);

    const latencyRows = useMemo(() => {
        const rows = [];
        for (const event of visible) {
            if (event.kind !== "bot_reply") continue;
            const ms = Number(event.payload?.latencyMs);
            if (!Number.isFinite(ms)) continue;
            rows.push({
                turnId: event.turnId,
                caseId: event.caseId,
                latencyMs: ms,
            });
        }
        return rows;
    }, [visible]);

    const maxLatency = useMemo(() => {
        let max = 0;
        for (const row of latencyRows) {
            if (row.latencyMs > max) max = row.latencyMs;
        }
        return max;
    }, [latencyRows]);

    const selectedRow = useMemo(() => {
        if (!selectedKey) return null;
        return safeResults.find((r) => rowKey(r) === selectedKey) || null;
    }, [safeResults, selectedKey]);

    const atEnd = totalMs > 0 && clockMs >= totalMs;

    useEffect(() => {
        if (typeof onClockChange !== "function") return;
        onClockChange({
            elapsedMs: clockMs,
            untilIso,
            atEnd,
        });
    }, [onClockChange, clockMs, untilIso, atEnd]);

    useEffect(() => {
        if (!playing || !t0Iso) return undefined;
        const id = setInterval(() => {
            const anchor = playAnchorMsRef.current;
            if (!anchor) return;
            const anchorIso = new Date(anchor).toISOString();
            const virtual =
                elapsedAtPlayRef.current + virtualElapsedMs(anchorIso, Date.now(), speed);
            const clamped = Math.min(virtual, totalMs);
            setElapsedMs(clamped);
            if (clamped >= totalMs) setPlaying(false);
        }, TICK_MS);
        return () => clearInterval(id);
    }, [playing, speed, t0Iso, totalMs]);

    useEffect(() => {
        if (selectedKey && !safeResults.some((r) => rowKey(r) === selectedKey)) {
            setSelectedKey(null);
        }
    }, [safeResults, selectedKey]);

    // No estado final, seleciona o último turno para o painel de snapshot ficar útil.
    useEffect(() => {
        if (selectedKey) return;
        if (!atEnd || safeResults.length === 0) return;
        const last = safeResults[safeResults.length - 1];
        if (last?.turnId == null) return;
        setSelectedKey(rowKey(last));
    }, [selectedKey, atEnd, safeResults]);

    const reset = () => {
        setPlaying(false);
        setElapsedMs(-1);
        setSelectedKey(null);
        playAnchorMsRef.current = null;
        elapsedAtPlayRef.current = 0;
    };

    const togglePlay = () => {
        if (playing) {
            setPlaying(false);
            return;
        }
        if (atEnd) {
            setElapsedMs(-1);
            elapsedAtPlayRef.current = -1;
        } else {
            elapsedAtPlayRef.current = clockMs;
        }
        playAnchorMsRef.current = Date.now();
        setPlaying(true);
    };

    const changeSpeed = (nextSpeed) => {
        if (playing) {
            elapsedAtPlayRef.current = clockMs;
            playAnchorMsRef.current = Date.now();
        }
        setSpeed(nextSpeed);
    };

    const selectBubble = (bubble, caseId) => {
        if (!bubble?.turnId) return;
        const resolvedCaseId = bubble.caseId || caseId;
        setSelectedKey(`${resolvedCaseId}:${bubble.turnId}`);
        if (!t0Iso) return;
        setPlaying(false);
        setElapsedMs(
            turnScrubOffsetMs(safeTimeline, t0Iso, bubble.turnId, resolvedCaseId)
        );
    };

    const bubbleGroups = [];
    const groupByCase = new Map();
    for (const bubble of bubbles) {
        const caseId = bubble.caseId || "default";
        if (!groupByCase.has(caseId)) {
            groupByCase.set(caseId, []);
        }
        groupByCase.get(caseId).push(bubble);
    }
    for (const [caseId, items] of groupByCase) {
        bubbleGroups.push({ caseId, bubbles: items });
    }

    const elapsedLabel =
        totalMs > 0
            ? `${Math.max(0, Math.round(clockMs / 100) / 10)}s / ${Math.round(totalMs / 100) / 10}s`
            : "0s";

    return (
        <div className="space-y-3" aria-label="Replay timed chat flow">
            <div className="flex flex-wrap items-center gap-2">
                <button
                    type="button"
                    aria-label="Reset replay"
                    onClick={reset}
                    className="rounded border border-gray-600 px-2 py-1 text-xs text-gray-200 hover:border-laranjinha"
                >
                    Reset
                </button>
                <button
                    type="button"
                    aria-label={playing ? "Pause replay" : "Play replay"}
                    onClick={togglePlay}
                    disabled={totalMs === 0}
                    className="rounded border border-gray-600 px-2 py-1 text-xs text-gray-200 hover:border-laranjinha disabled:opacity-40"
                >
                    {playing ? "Pause" : "Play"}
                </button>
                {speedOptions.map((option) => (
                    <button
                        key={option}
                        type="button"
                        aria-label={`Velocidade ${option}×`}
                        aria-pressed={speed === option}
                        onClick={() => changeSpeed(option)}
                        className={`rounded border px-2 py-1 text-xs ${
                            speed === option
                                ? "border-laranjinha text-laranjinha"
                                : "border-gray-600 text-gray-200 hover:border-laranjinha"
                        }`}
                    >
                        {option}×
                    </button>
                ))}
                <span className="text-xs text-gray-500">{elapsedLabel}</span>
            </div>

            {maxLatency > 0 ? (
                <div className="flex flex-wrap gap-1" aria-label="Barra de latência">
                    {latencyRows.map((row) => {
                        const pct = Math.max(8, Math.round((row.latencyMs / maxLatency) * 100));
                        return (
                            <div
                                key={`lat-${row.caseId}-${row.turnId}`}
                                title={`${row.latencyMs}ms`}
                                className="h-1.5 rounded bg-green-500/70"
                                style={{ width: `${pct}px`, minWidth: "8%" }}
                            />
                        );
                    })}
                </div>
            ) : null}

            {bubbleGroups.length === 0 ? (
                <p className="text-sm text-gray-400">
                    Nenhum evento revelado. Use Play para reassistir.
                </p>
            ) : (
                bubbleGroups.map((group) => {
                    const meta = caseById.get(group.caseId);
                    const title = meta?.description || group.caseId || "Cenário";
                    return (
                        <div key={group.caseId} className="mb-4">
                            <h5 className="text-xs font-medium text-laranjinha mb-2">
                                {title}
                            </h5>
                            {group.bubbles.map((bubble, index) => {
                                const key = `${bubble.role}-${bubble.turnId || index}`;
                                const selected =
                                    selectedKey &&
                                    bubble.turnId &&
                                    selectedKey ===
                                        `${bubble.caseId || group.caseId}:${bubble.turnId}`;
                                return (
                                    <ChatBubble
                                        key={key}
                                        role={bubble.role}
                                        text={bubble.text}
                                        pass={bubble.pass}
                                        selected={selected}
                                        onClick={
                                            bubble.role !== "pending"
                                                ? () => selectBubble(bubble, group.caseId)
                                                : undefined
                                        }
                                        testId={
                                            bubble.role === "pending"
                                                ? `timed-pending-${bubble.turnId}`
                                                : `timed-${bubble.role}-${bubble.turnId}`
                                        }
                                    />
                                );
                            })}
                        </div>
                    );
                })
            )}

            {traceSteps.length > 0 ? (
                <ul
                    className="rounded border border-gray-700 bg-gray-900/40 p-2 text-xs space-y-1"
                    aria-label="Trilha trace_step"
                >
                    {traceSteps.map((step, index) => {
                        const stage =
                            step.payload?.stage || step.label || `step-${index + 1}`;
                        const status = step.payload?.status;
                        const isCurrent = step === currentTrace;
                        return (
                            <li
                                key={`${step.turnId}-${stage}-${index}`}
                                data-current={isCurrent ? "true" : "false"}
                                className={
                                    isCurrent
                                        ? "text-laranjinha font-medium"
                                        : "text-gray-500"
                                }
                            >
                                {stage}
                                {status ? ` (${status})` : ""}
                            </li>
                        );
                    })}
                </ul>
            ) : null}

            {selectedRow ? (
                <div
                    className="rounded border border-gray-700 bg-gray-800/50 p-3 space-y-2 text-xs text-gray-300"
                    aria-label="Detalhe do turno selecionado"
                >
                    <p>
                        <span className="text-gray-500">input:</span>{" "}
                        {selectedRow.input || "—"}
                    </p>
                    <div>
                        <span className="text-gray-500">output:</span>
                        <pre className="mt-1 whitespace-pre-wrap break-words text-gray-400 bg-gray-950/50 p-2 rounded">
                            {selectedRow.output || "—"}
                        </pre>
                    </div>
                    {selectedRow.latencyMs != null ? (
                        <p>
                            <span className="text-gray-500">latência:</span>{" "}
                            {selectedRow.latencyMs}ms
                        </p>
                    ) : null}
                    {selectedRow.intent ? (
                        <p>
                            <span className="text-gray-500">intent:</span>{" "}
                            {selectedRow.intent}
                        </p>
                    ) : null}
                    {selectedRow.debounceEvidence ? (
                        <div data-testid="timed-debounce-evidence">
                            <p className="text-gray-500">rajada (debounce)</p>
                            <div className="mt-1 flex flex-wrap gap-1.5">
                                {debounceEvidenceChips(selectedRow.debounceEvidence).map(
                                    (chip) => (
                                        <div
                                            key={chip.label}
                                            className="rounded border border-gray-600/70 bg-gray-900/70 px-2 py-1"
                                        >
                                            <p className="text-[9px] uppercase tracking-wide text-gray-500">
                                                {chip.label}
                                            </p>
                                            <p className="text-xs text-gray-200">
                                                {chip.value}
                                            </p>
                                        </div>
                                    )
                                )}
                            </div>
                        </div>
                    ) : null}
                    {selectedRow.chatwootSnapshot ? (
                        <>
                            <FlowChatwootChips snapshot={selectedRow.chatwootSnapshot} />
                            <details>
                                <summary className="cursor-pointer text-gray-400 hover:text-gray-200">
                                    Chatwoot snapshot
                                </summary>
                                <pre className="mt-1 whitespace-pre-wrap break-words text-gray-500 bg-gray-950/50 p-2 rounded">
                                    {JSON.stringify(selectedRow.chatwootSnapshot, null, 2)}
                                </pre>
                            </details>
                        </>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
